import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { EditorialInboxBoard } from '@api/BoardSDK';
import { executeIntegration } from '@api/integration-block.server';
import { withBoardAuth } from '@generated/server/boardAuth.server';
import { refreshExternalToken } from '@/services/token.service';

const GMAIL = 'c70cb228-a141-49d4-a586-364172111780';
// George Fiala's monday user id (salesperson on the monthly ad lists).
const GEORGE_ID = '106410322';

const Input = z.object({
  type: z.enum(['News Tip', 'Suggestion', 'Story Idea', 'Letter to the Editor']),
  neighborhood: z.string().trim().max(120).optional(),
  paper: z.enum(['Red Hook Star-Revue', 'The Village Star-Revue', 'Either / Both']),
  message: z.string().trim().min(5).max(8000),
  anonymous: z.boolean(),
  senderName: z.string().trim().max(120).optional(),
  email: z.string().trim().max(200).optional(),
  phone: z.string().trim().max(40).optional(),
});

async function emailTip(subject: string, body: string): Promise<{ ok: boolean; why?: string }> {
  let why = "";
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      // Second try strips non-ASCII characters (e.g. ellipsis, dots, accents) that some mail gateways reject in headers.
      const ascii = (s: string) => s.normalize("NFKD").replace(/[\u2026]/g, "...").replace(/[\u00b7]/g, "-").replace(/[^\x09\x0a\x0d\x20-\x7e]/g, "");
      const res = await withBoardAuth(() => executeIntegration(GMAIL, { to: ["gbrook8344@gmail.com"], subject: attempt === 0 ? ascii(subject) : "New editorial tip from the portal", body: attempt === 0 ? body : ascii(body) }));
      console.log('[editorial] email sent', { messageId: res?.messageId });
      return { ok: true };
    } catch (e) {
      const err = e as { code?: string; userMessage?: string; message?: string; cause?: unknown };
      console.error('[editorial] email failed', { attempt, code: err.code, userMessage: err.userMessage, message: err.message, cause: err.cause });
      const cause = err.cause as { message?: string; code?: string } | undefined;
      why = [err.code, err.userMessage || err.message, cause?.code, cause?.message].filter(Boolean).join(' | ').slice(0, 300) || 'unknown error';
      if (attempt === 0) {
        // Public visitors use a shared connection; refresh it before the retry (same as announcements).
        try { await refreshExternalToken(); } catch (re) { console.error("[editorial] refresh failed", re); }
        await new Promise((r) => setTimeout(r, 1000));
      }
    }
  }
  return { ok: false, why };
}

/** Saves a reader's tip / suggestion / idea to the Editorial Inbox board and emails George. */
export const sendEditorialMessage = createServerFn({ method: 'POST' })
  .validator((d: unknown) => Input.parse(d))
  .handler(async ({ data }) => {
    const anon = data.anonymous;
    const subject = data.message.replace(/\s+/g, ' ').slice(0, 60) + (data.message.length > 60 ? '...' : '');
    const who = anon ? 'Anonymous' : [data.senderName, data.neighborhood, data.email, data.phone].filter(Boolean).join(' - ') || 'No contact given';

    let id: string | null = null;
    try {
      const item = await withBoardAuth(() => new EditorialInboxBoard().item().create({
        name: `${data.type}: ${subject}`,
        status: 'New',
        type: data.type === 'Letter to the Editor' ? null : data.type,
        paper: data.paper,
        message: data.type === 'Letter to the Editor' ? `LETTER TO THE EDITOR
From: ${data.senderName ?? ''}${data.neighborhood ? ', ' + data.neighborhood : ''}

${data.message}` : data.message,
        anonymous: anon,
        senderName: anon ? null : data.senderName || null,
        email: !anon && data.email ? { email: data.email, label: data.email } : null,
        phone: !anon && data.phone ? { phone: data.phone, country: 'US' } : null,
        received: new Date(),
      }).execute());
      id = item.id;
    } catch (e) {
      console.error('[editorial] create failed', e);
    }

    // Email goes out even if the board save failed, so no tip is lost.
    const emailed = await emailTip(
      `New ${data.type} (${data.paper}): ${subject}`,
      `${data.type} for ${data.paper}\nFrom: ${who}\n\n${data.message}\n\n${id ? 'Saved on the Editorial Inbox board in monday. Any photos or files are attached there.' : 'NOTE: this tip could not be saved to the Editorial Inbox board.'}`,
    );

    // Always post the FULL tip as an update that @mentions George: readable and copyable in monday, and monday emails it to him.
    let notified = false;
    if (id) {
      // Post the FULL text as an update that @mentions George: monday emails mentions with the
      // whole update, so the letter arrives in his inbox ready to copy into the paper.
      const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const html = `<p><strong>${esc(data.type)}</strong> for ${esc(data.paper)}<br>From: ${esc(who)}</p><p>${esc(data.message).replace(/\n/g, '<br>')}</p>`;
      // (Passing mentions to post() fails on monday's side, so post plain, then ring George's bell.)
      try { await withBoardAuth(() => new EditorialInboxBoard().item(id as string).post().create(html).execute()); }
      catch (e) { console.error('[editorial] update post failed', e); }
      {
        try {
          await withBoardAuth(() => new EditorialInboxBoard().item(id as string).notify(GEORGE_ID).create(`New ${data.type} for ${data.paper}: ${subject}`).execute());
          notified = true;
        } catch (e2) { console.error('[editorial] monday notification failed', e2); }
      }
    }

    if (!id && !emailed.ok) throw new Error('Your message could not be sent right now. Please try again, or email gbrook8344@gmail.com.');
    return { id, emailed: emailed.ok, notified, emailWhy: emailed.why ?? null };
  });

/** Attaches a reader's photo or file to their editorial message. */
export const uploadEditorialFile = createServerFn({ method: 'POST' })
  .validator((data: FormData) => data)
  .handler(async ({ data }) => {
    const file = data.get('file') as File | null;
    const itemId = String(data.get('itemId') ?? '');
    if (!file || !/^\d+$/.test(itemId)) throw new Error('Missing file');
    return withBoardAuth(() => new EditorialInboxBoard().item(itemId).uploadFile({ columnId: 'file_mm819wry' as 'attachments', file }).execute());
  });
